import { useLayoutEffect, useRef, useState } from 'react';
import { useMutation, useMutationState, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { quoteRequestApi } from '../api/endpoints.ts';
import { SESSION_QUERY_KEY } from '../state/session.tsx';
import type { Paginated, QuoteRequest, SessionResponse } from '@shared/types/index.ts';

export type RequestAction = 'convert' | 'dismiss';
type Operation = { id: string; action: RequestAction; generation: number };

export function requestActionScope(session: {
  user: { id: string } | null;
  company: { id: string } | null;
  location?: { id: string } | null;
  impersonatedBy?: string | null;
}) {
  return JSON.stringify([session.user?.id, session.company?.id, session.location?.id, session.impersonatedBy]);
}

/** One lifetime per session scope (the Requests page is keyed by that scope). */
export function useRequestActions(scope: string, view: string) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const mounted = useRef(false);
  const viewGeneration = useRef(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const mutationKey = ['request-actions', scope];
  // Read every outstanding operation, not a mutation observer's most recent
  // variables. This also retains row locks when leaving and reopening Requests.
  const operations = useMutationState({
    filters: { mutationKey, status: 'pending' },
    select: (mutation) => mutation.state.variables as Operation,
  });
  const pending = Object.fromEntries(operations.map(({ id, action }) => [id, action]));

  useLayoutEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useLayoutEffect(() => {
    viewGeneration.current += 1;
    setErrors({});
  }, [view]);

  // SessionProvider publishes this query before changing the rendered scope.
  // Its lifetime spans routes; the originating Requests mount does not.
  const sameSession = () => {
    const session = queryClient.getQueryData<SessionResponse | null>(SESSION_QUERY_KEY);
    return Boolean(session && requestActionScope(session) === scope);
  };
  const sameView = (operation: Operation) =>
    mounted.current && sameSession() && operation.generation === viewGeneration.current;
  const mutation = useMutation({
    mutationKey,
    retry: false,
    // An offline click must fail for an explicit retry, not queue a write
    // that could resume after the operator has switched account/location.
    networkMode: 'always',
    mutationFn: async ({ id, action }: Operation) => {
      if (!mounted.current || !sameSession()) {
        throw new Error('The Requests session changed before the action started.');
      }
      return action === 'convert' ? quoteRequestApi.convert(id) : quoteRequestApi.dismiss(id);
    },
    onSuccess: (result, operation) => {
      const request = 'request' in result ? result.request : result;
      // Only this session's cache is eligible, even after leaving the page.
      // Retire the row's controls before refreshing, including when a refresh
      // fails: a successful mutation must not become available to retry.
      queryClient.setQueriesData<Paginated<QuoteRequest>>({ queryKey: ['quote-requests', scope] }, (current) =>
        current
          ? {
              ...current,
              // Mutation DTOs omit the list's photo-intake enrichment. Keep
              // its review gate, links and status until the list refreshes.
              items: current.items.map((item) =>
                item.id === operation.id
                  ? {
                      ...item,
                      status: request.status,
                      convertedCustomerId: request.convertedCustomerId,
                      convertedQuoteId: request.convertedQuoteId,
                      handledByUserId: request.handledByUserId,
                      handledAt: request.handledAt,
                    }
                  : item,
              ),
            }
          : current,
      );
      const refreshCurrentSession = sameSession();
      if (refreshCurrentSession) {
        void queryClient.invalidateQueries({ queryKey: ['quote-requests-new-count'] });
        void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      }
      // Refresh in the background: a completed write must release its lock and
      // open its quote even if the follow-up read stalls.
      void queryClient.invalidateQueries({
        queryKey: ['quote-requests', scope],
        // A departed session must not issue a read with the new session's cookie.
        refetchType: refreshCurrentSession ? 'active' : 'none',
      });
      if (sameView(operation) && 'quoteId' in result) navigate(`/quotes/${result.quoteId}`);
    },
    onError: (error, operation) => {
      if (sameView(operation)) setErrors((current) => ({ ...current, [operation.id]: error.message }));
    },
  });

  function run(id: string, action: RequestAction) {
    if (!mounted.current || !sameSession()) return;
    // The cache changes synchronously on mutate, before React paints disabled
    // buttons. It fences rapid/keyboard activations and conflicting row actions.
    const inFlight = queryClient
      .getMutationCache()
      .findAll({ mutationKey, status: 'pending' })
      .map((entry) => entry.state.variables as Operation);
    // A conversion leaves the inbox: two conversions cannot race to choose
    // the quote editor. Independent dismissals can still overlap.
    if (inFlight.some((entry) => entry.id === id || (action === 'convert' && entry.action === 'convert'))) return;
    setErrors((current) => {
      const next = { ...current };
      delete next[id];
      return next;
    });
    mutation.mutate({ id, action, generation: viewGeneration.current });
  }

  return { pending, errors, converting: operations.some(({ action }) => action === 'convert'), run };
}
